// app/shared/components/cards/HTFBaseCard.tsx
//
// HowTheF's titled card, drawn on fluxos-lib-ui's FLPanel: the kit surface,
// radius and stamp edge, no colour bar across the top and no hue-filled card.
// `variant` now only names the tone of the badge (a soft FLBadge, never the
// yellow primary). The icon sits in a sunken ink tile, the kit's icon tile.
// Props are unchanged so call sites keep working; `accentBar` is accepted and
// ignored.
"use client";

import {
  FLBadge,
  FLPanel,
  FLPanelContent,
  FLPanelDescription,
  FLPanelHeader,
  FLPanelTitle,
} from "@ogattermayr/fluxos-lib-ui/kit";
import type { LucideIcon } from "lucide-react";
import React from "react";

import { cn } from "@/lib/utils";

export type HTFCardVariant =
  | "default"
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "teal"
  | "yellow";

export interface HTFBaseCardProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  icon?: LucideIcon;
  badge?: string;
  variant?: HTFCardVariant;
  contentClassName?: string;
  className?: string;
  headerClassName?: string;
  footerContent?: React.ReactNode;
  onClick?: () => void;
  /** @deprecated Cards carry no colour bar; accepted and ignored. */
  accentBar?: boolean;
  compact?: boolean;
}

type BadgeLook = React.ComponentProps<typeof FLBadge>;

/** The badge each variant draws: a status tone or a hue, never yellow. */
const VARIANT_BADGE: Record<HTFCardVariant, Pick<BadgeLook, "tone" | "hue">> = {
  default: { tone: "neutral" },
  primary: { tone: "neutral" },
  yellow: { tone: "neutral" },
  secondary: { tone: "ai" },
  success: { tone: "healthy" },
  warning: { hue: "clay" },
  error: { tone: "critical" },
  info: { tone: "attention" },
  teal: { tone: "info" },
};

export function HTFBaseCard({
  children,
  title,
  description,
  icon: Icon,
  badge,
  variant = "default",
  contentClassName,
  className,
  headerClassName,
  footerContent,
  onClick,
  compact = false,
}: HTFBaseCardProps) {
  const padding = compact ? "sm" : "md";
  const hasHeader = Boolean(title || Icon || badge);
  const interactive = Boolean(onClick);

  return (
    <FLPanel
      padding={padding}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        "flex flex-col overflow-hidden",
        interactive &&
          "cursor-pointer transition-shadow duration-fl-hover ease-fl-out hover:shadow-fl-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fl-focus",
        className,
      )}
    >
      {hasHeader && (
        <FLPanelHeader
          padding={padding}
          className={cn("items-start gap-3", headerClassName)}
        >
          <div className="flex min-w-0 flex-1 items-start gap-3">
            {Icon && (
              <span
                aria-hidden="true"
                className="grid size-10 shrink-0 place-items-center rounded-fl-md bg-fl-sunken text-fl-ink shadow-fl-flat"
              >
                <Icon size={20} />
              </span>
            )}
            {(title || description) && (
              <div className="min-w-0 flex-1">
                {title && <FLPanelTitle>{title}</FLPanelTitle>}
                {description && (
                  <FLPanelDescription>{description}</FLPanelDescription>
                )}
              </div>
            )}
          </div>
          {badge && (
            <FLBadge size="sm" {...VARIANT_BADGE[variant]}>
              {badge}
            </FLBadge>
          )}
        </FLPanelHeader>
      )}

      <FLPanelContent
        padding={padding}
        hasHeader={hasHeader}
        className={cn("flex-grow", contentClassName)}
      >
        {children}
      </FLPanelContent>

      {footerContent && (
        <div
          className={cn(
            "border-t border-fl-line",
            compact ? "p-4" : "p-5 md:p-6",
          )}
        >
          {footerContent}
        </div>
      )}
    </FLPanel>
  );
}
